import { motion, AnimatePresence } from "framer-motion"
import { useEffect, useState } from "react"
import { useBrandStore } from "@/stores/brand"
import { BrandLogo } from "./brand-logo"

interface SplashScreenProps {
  onComplete?: () => void
  minDuration?: number
}

export function SplashScreen({ onComplete, minDuration = 2000 }: SplashScreenProps) {
  const [isVisible, setIsVisible] = useState(true)
  const { brandConfig } = useBrandStore()

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => {
        onComplete?.()
      }, 500)
    }, minDuration)

    return () => clearTimeout(timer)
  }, [minDuration, onComplete])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
        >
          {/* Background animated circles */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full bg-primary/20 blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1.5,
              }}
              className="absolute -bottom-1/2 -right-1/2 w-full h-full rounded-full bg-primary/20 blur-3xl"
            />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center gap-8">
            {/* Logo */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                delay: 0.2,
              }}
              className="relative"
            >
              {/* Glow effect */}
              <motion.div
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(var(--primary-rgb), 0.3)",
                    "0 0 60px rgba(var(--primary-rgb), 0.5)",
                    "0 0 20px rgba(var(--primary-rgb), 0.3)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 rounded-full"
              />
              
              <BrandLogo
                className="w-32 h-32 drop-shadow-2xl"
                imgClassName="w-full h-full object-contain"
                fallbackClassName="rounded-2xl shadow-2xl"
                fallbackIcon={
                  <span className="text-4xl font-bold text-white">
                    {brandConfig?.name?.charAt(0) || "P"}
                  </span>
                }
              />
            </motion.div>

            {/* Brand name */}
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-3xl font-bold text-slate-900 tracking-wider"
            >
              {brandConfig?.name || "Oficina Virtual"}
            </motion.h1>

            {/* Loading indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col items-center gap-4"
            >
              {/* Animated dots */}
              <div className="flex gap-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      y: [0, -10, 0],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: i * 0.15,
                      ease: "easeInOut",
                    }}
                    className="w-3 h-3 rounded-full bg-primary"
                  />
                ))}
              </div>
              
              <motion.p
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-sm text-slate-500"
              >
                Cargando...
              </motion.p>
            </motion.div>
          </div>

          {/* Version */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-8 text-slate-400 text-xs"
          >
            v2.0.0
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
